import i18n from "../../i18n";
import React, { useState, useEffect, useMemo } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { canAccessDashboardPath } from "@/lib/routeHome";
import { useTranslation } from "react-i18next";

const AI_ENABLED = process.env.REACT_APP_AI_ENABLED !== "0";
import { Button } from "@/components/ui/button";
import {
    LayoutDashboard, PawPrint, Settings, FileBarChart, Wallet,
    Boxes, Bell, LogOut, Menu, X, Sun, Moon, Sprout,
    Target, Scale, Users, Kanban, CalendarDays, QrCode, Sparkles, HardHat, PieChart,
    ShoppingCart, Receipt, UserCircle, ClipboardList, Truck, TrendingUp as TrendingUpIcon,
    LineChart as LineChartIcon, Layers, Smartphone, Hammer, Baby, Wheat, Network, Fish,
    ClipboardCheck, Cog, ShieldCheck, AlertTriangle, ShoppingBag, ChevronDown, Trash2
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import AlertsBell from "@/components/AlertsBell";
import BottomNav from "@/components/BottomNav";
import AJFarmLogo, { AJEmblem } from "@/components/brand/AJFarmLogo";
import BrandFooter from "@/components/brand/BrandFooter";
import LanguageSelector from "@/components/LanguageSelector";

// NAV_GROUPS uses i18n keys (label_key). Everything is translated at render time so
// changing language via the sidebar dropdown re-labels the menu instantly.
const NAV_GROUPS = [
    {
        label_key: "groups.overview", label: "Overview",
        items: [
            { to: "/", label_key: "nav.overview", label: "Overview", icon: PieChart, perm: "view", testid: "nav-overview" },
            { to: "/dashboard", label_key: "nav.dashboard", label: "Dashboard", icon: LayoutDashboard, perm: "view", testid: "nav-dashboard" },
            { to: "/alerts", label_key: "nav.alerts", label: "Alerts", icon: Bell, perm: "view", testid: "nav-alerts", showBadge: true },
        ],
    },
    {
        label_key: "groups.livestock", label: "Livestock",
        items: [
            { to: "/livestock", label_key: "nav.livestock_hub", label: "Livestock Hub", icon: PawPrint, perm: "view", testid: "nav-livestock-hub" },
            { to: "/species", label_key: "nav.species", label: "Species", icon: PawPrint, perm: "view", testid: "nav-species" },
            { to: "/birth-to-sale", label_key: "nav.birth_to_sale", label: "Birth-to-Sale", icon: Baby, perm: "view", testid: "nav-birth-to-sale" },
            { to: "/growth", label_key: "nav.growth", label: "Growth & FCR", icon: TrendingUpIcon, perm: "add", testid: "nav-growth" },
            { to: "/break-even", label_key: "nav.break_even", label: "Break-Even", icon: Scale, perm: "manage_finance", testid: "nav-break-even" },
        ],
    },
    {
        label_key: "groups.crops", label: "Crops",
        items: [
            { to: "/crops", label_key: "nav.crops", label: "Crops & Fields", icon: Wheat, perm: "view", testid: "nav-crops" },
            { to: "/aquaculture", label_key: "nav.aquaculture", label: "Aquaculture", icon: Fish, perm: "view", testid: "nav-aquaculture" },
        ],
    },
    {
        label_key: "groups.sales", label: "Sales & POS",
        items: [
            { to: "/sales", label_key: "nav.sales", label: "Sales", icon: Receipt, perm: "add", testid: "nav-sales" },
            { to: "/pos", label_key: "nav.pos", label: "POS · Sell Now", icon: ShoppingCart, perm: "add", testid: "nav-pos" },
            { to: "/shifts", label_key: "nav.shifts", label: "Shifts · Cash Drawer", icon: ClipboardList, perm: "add", testid: "nav-shifts" },
            { to: "/customers", label_key: "nav.customers", label: "Customers", icon: UserCircle, perm: "add", testid: "nav-customers" },
            { to: "/deliveries", label_key: "nav.deliveries", label: "Deliveries", icon: Truck, perm: "add", testid: "nav-deliveries" },
            { to: "/sales-dashboard", label_key: "nav.sales_dashboard", label: "Sales Dashboard", icon: LineChartIcon, perm: "view", testid: "nav-sales-dashboard" },
            { to: "/sales-inventory", label_key: "nav.sales_inventory", label: "Sales & Inventory", icon: Receipt, perm: "manage_inventory", testid: "nav-sales-inventory" },
            { to: "/sales-targets", label_key: "nav.sales_targets", label: "Sales Targets", icon: Target, perm: "manage_finance", testid: "nav-sales-targets" },
        ],
    },
    {
        label_key: "groups.finance", label: "Inventory & Finance",
        items: [
            { to: "/feed-inventory", label_key: "nav.feed_inventory", label: "Feed Inventory", icon: Sprout, perm: "manage_inventory", testid: "nav-feed-inventory" },
            { to: "/purchasing", label_key: "nav.purchasing", label: "Purchasing", icon: ShoppingBag, perm: "manage_inventory", testid: "nav-purchasing" },
            { to: "/inventory", label_key: "nav.inventory", label: "Inventory", icon: Boxes, perm: "manage_inventory", testid: "nav-inventory" },
            { to: "/batches", label_key: "nav.batches", label: "Product Batches", icon: Layers, perm: "manage_inventory", testid: "nav-batches" },
            { to: "/financial", label_key: "nav.financial", label: "Financial", icon: Wallet, perm: "manage_finance", testid: "nav-financial" },
            { to: "/workforce-analytics", label_key: "nav.workforce_analytics", label: "Workforce & Earnings", icon: Users, ownerOnly: true, testid: "nav-workforce-analytics" },
            { to: "/reports", label_key: "nav.reports", label: "Reports", icon: FileBarChart, perm: "manage_reports", testid: "nav-reports" },
        ],
    },
    {
        label_key: "groups.facility", label: "Facility",
        items: [
            { to: "/fleet", label_key: "nav.fleet", label: "Fleet Management", icon: Truck, perm: "manage_settings", testid: "nav-fleet" },
            { to: "/equipment", label_key: "nav.equipment", label: "Machinery & Equipment", icon: Cog, perm: "manage_settings", testid: "nav-equipment" },
            { to: "/construction", label_key: "nav.construction", label: "Construction", icon: Hammer, perm: "manage_settings", testid: "nav-construction" },
        ],
    },
    {
        label_key: "groups.daily_ops", label: "Daily Ops",
        items: [
            { to: "/tasks", label_key: "nav.tasks", label: "Tasks", icon: Kanban, perm: "add", testid: "nav-tasks" },
            { to: "/calendar", label_key: "nav.calendar", label: "Calendar", icon: CalendarDays, perm: "view", testid: "nav-calendar" },
            { to: "/field", label_key: "nav.field_mode", label: "Field Mode", icon: Smartphone, perm: "add", testid: "nav-field" },
            { to: "/forms", label_key: "nav.forms", label: "Forms & Assessments", icon: ClipboardCheck, perm: "view", testid: "nav-forms" },
            { to: "/incidents", label_key: "nav.incidents", label: "Incident Reports", icon: AlertTriangle, perm: "view", testid: "nav-incidents" },
            { to: "/scan", label_key: "nav.scan", label: "QR Scanner", icon: QrCode, perm: "view", testid: "nav-scan" },
            { to: "/worker", label_key: "nav.worker_tasks", label: "My Tasks", icon: HardHat, perm: "view", testid: "nav-worker" },
        ],
    },
    {
        label_key: "groups.system", label: "System",
        items: [
            { to: "/employees", label_key: "nav.employees", label: "Employees", icon: Users, ownerOnly: true, testid: "nav-employees" },
            { to: "/attendance", label_key: "nav.attendance", label: "Attendance", icon: ClipboardList, ownerOnly: true, testid: "nav-attendance" },
            { to: "/payroll", label_key: "nav.payroll", label: "Payroll (PH)", icon: Wallet, perm: "manage_finance", testid: "nav-payroll-system" },
            { to: "/hr", label_key: "nav.hr", label: "HR & Training", icon: Network, perm: "manage_users", testid: "nav-hr" },
            { to: "/notifications", label_key: "nav.notifications", label: "Notifications", icon: Bell, perm: "view", testid: "nav-notifications" },
            { to: "/admin", label_key: "nav.admin", label: "Admin Settings", icon: Settings, perm: "manage_settings", testid: "nav-admin" },
            { to: "/admin/users", label_key: "nav.admin_users", label: "Admin Dashboard", icon: ShieldCheck, ownerOnly: true, testid: "nav-admin-users" },
            { to: "/admin/sync", label_key: "nav.sync_monitor", label: "Sync Monitor", icon: Smartphone, ownerOnly: true, testid: "nav-sync-monitor" },
            { to: "/admin/recycle-bin", label_key: "nav.recycle_bin", label: "Recycle Bin", icon: Trash2, strictOwner: true, testid: "nav-recycle-bin" },
        ],
    },
];

export default function AppLayout() {
    const { user, logout, can, isOwner } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
    const [farm, setFarm] = useState({});
    const [alertCount, setAlertCount] = useState(0);
    const [expandedGroups, setExpandedGroups] = useState(() => new Set(["groups.overview"]));

    useEffect(() => {
        import("@/lib/api").then(({ api }) => api.get("/farm").then((r) => {
            setFarm(r.data);
            const root = document.documentElement;
            if (r.data.primary_hsl) root.style.setProperty("--primary", r.data.primary_hsl);
            if (r.data.accent_hsl) root.style.setProperty("--accent", r.data.accent_hsl);
        }));
    }, []);

    const { t } = useTranslation();

    // Poll alert count every 60s so the sidebar badge reflects reality.
    useEffect(() => {
        let mounted = true;
        const load = () => {
            import("@/lib/api").then(({ api }) => api.get("/alerts").then((r) => {
                if (mounted) setAlertCount((r.data.counts?.critical || 0) + (r.data.counts?.warning || 0));
            }).catch(() => null));
        };
        load();
        const t = setInterval(load, 60000);
        return () => { mounted = false; clearInterval(t); };
    }, []);

    const toggleTheme = () => {
        const el = document.documentElement;
        el.classList.toggle("dark");        setDark(el.classList.contains("dark"));
    };

    const doLogout = async () => { await logout(); nav("/login"); };

    const visibleGroups = useMemo(() => (
        NAV_GROUPS
            .map((g) => ({ ...g, items: g.items.filter((n) => n.enabled !== false && canAccessDashboardPath(user, n.to) && (n.strictOwner ? (user?.role_name || "").trim().toLowerCase() === "owner" : n.ownerOnly ? isOwner() : can(n.perm))) }))
            .filter((g) => g.items.length > 0)
    ), [can, isOwner, user]);

    useEffect(() => {
        const activeGroup = visibleGroups.find((group) =>
            group.items.some((item) => item.to === location.pathname || (item.to !== "/" && location.pathname.startsWith(`${item.to}/`)))
        );
        if (!activeGroup) return;
        setExpandedGroups((current) => {
            if (current.has(activeGroup.label_key)) return current;
            const next = new Set(current);
            next.add(activeGroup.label_key);
            return next;
        });
    }, [location.pathname, visibleGroups]);

    const toggleGroup = (groupKey) => {
        setExpandedGroups((current) => {
            const next = new Set(current);
            if (next.has(groupKey)) next.delete(groupKey);
            else next.add(groupKey);
            return next;
        });
    };

    return (
        <div className="min-h-screen flex bg-background text-foreground aj-app-shell">
            {/* Sidebar */}
            <aside
                data-testid="app-sidebar"
                className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 left-0 z-40 h-screen w-72 aj-sidebar transition-transform duration-200 flex flex-col`}
            >
                <div className="px-5 py-5 border-b border-white/10">
                    <AJFarmLogo variant="compact" />
                </div>
                <nav className="aj-sidebar-scroll flex-1 p-3 space-y-1.5 overflow-y-auto" data-testid="app-sidebar-nav">
                    {AI_ENABLED && canAccessDashboardPath(user, "/ai-katulong") && can("view") ? (
                        <NavLink
                            to="/ai-katulong"
                            data-testid="nav-assistant"
                            onClick={() => setOpen(false)}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 h-10 rounded-xl text-sm font-semibold transition-colors ${
                                    isActive
                                        ? "aj-sidebar-link-active"
                                        : "text-aj-gold hover:text-white hover:bg-white/[0.07]"
                                }`
                            }
                        >
                            <Sparkles className="w-5 h-5" />
                            <span className="flex-1">{t("nav.assistant", "AI Katulong")}</span>
                        </NavLink>
                    ) : null}
                    {visibleGroups.map((g) => (
                        <div key={g.label_key || g.label} className="space-y-1">
                            <button
                                type="button"
                                onClick={() => toggleGroup(g.label_key)}
                                className="w-full h-9 px-3 rounded-lg flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.17em] text-white/45 hover:text-aj-gold hover:bg-white/[0.05] transition-colors"
                                aria-expanded={expandedGroups.has(g.label_key)}
                                aria-label={t("nav.toggle_group", { group: t(g.label_key, g.label) })}
                            >
                                <span>{t(g.label_key, g.label)}</span>
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expandedGroups.has(g.label_key) ? "rotate-180 text-aj-gold" : ""}`} />
                            </button>
                            <div className={expandedGroups.has(g.label_key) ? "space-y-1" : "hidden"}>
                            {g.items.map((n) => (
                                <NavLink
                                    key={n.to}
                                    to={n.to}
                                    end={n.to === "/"}
                                    data-testid={n.testid}
                                    onClick={() => setOpen(false)}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 px-4 h-10 rounded-xl text-sm font-medium transition-colors ${
                                            isActive
                                                ? "aj-sidebar-link-active"
                                                : "text-white/75 hover:text-white hover:bg-white/[0.07]"
                                        }`
                                    }
                                >
                                    <n.icon className="w-5 h-5" />
                                    <span className="flex-1">{t(n.label_key, n.label)}</span>
                                    {n.showBadge && alertCount > 0 && (
                                        <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-destructive text-destructive-foreground text-xs font-bold" data-testid="nav-alerts-badge">
                                            {alertCount > 99 ? "99+" : alertCount}
                                        </span>
                                    )}
                                </NavLink>
                            ))}
                            </div>
                        </div>
                    ))}
                </nav>
                <div className="p-4 border-t border-white/10 text-white">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-accent text-accent-foreground grid place-items-center font-bold">
                            {(user?.name || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">{user?.name}</div>
                            <div className="text-xs text-white/50 truncate">{user?.role_name}</div>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <LanguageSelector compact testId="sidebar-language-selector" className="[&_select]:w-[92px] [&_select]:border-white/15 [&_select]:bg-white/[0.07] [&_select]:text-white" />
                        <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 h-10 border-white/15 bg-white/[0.05] text-white hover:bg-white/10 hover:text-white"
                            onClick={toggleTheme}
                            data-testid="toggle-theme-btn"
                        >
                            {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        </Button>
                        <div className="flex-1 h-10 grid place-items-center border border-white/15 rounded-md">
                            <AlertsBell variant="desktop" />
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 h-10 border-white/15 bg-white/[0.05] text-white hover:bg-white/10 hover:text-white"
                            onClick={doLogout}
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </aside>

            {open && (
                <div
                    onClick={() => setOpen(false)}
                    className="fixed inset-0 bg-black/40 z-30 lg:hidden"
                />
            )}

            <div className="flex-1 flex flex-col min-w-0">
                <header className="lg:hidden sticky top-0 z-20 aj-mobile-header h-14 flex items-center px-3 gap-2 text-white">
                    <Button variant="ghost" size="icon" onClick={() => setOpen(true)} data-testid="open-sidebar-btn">
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </Button>
                    <AJEmblem className="w-9 h-9" />
                    <div className="min-w-0">
                        <div className="font-bold leading-none">{i18n.t("final.de7b27e508f3")}</div>
                        <div className="text-[8px] tracking-[0.18em] text-aj-gold mt-1">{i18n.t("final.a77a70c1dafa")}</div>
                    </div>
                    <div className="ml-auto flex items-center gap-1">
                        <AlertsBell variant="mobile" />
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-9 px-2 text-xs font-semibold"
                            onClick={doLogout}
                            data-testid="mobile-logout-btn"
                            aria-label={t("common.logout")}
                        >
                            <LogOut className="w-4 h-4 mr-1" />{t("common.logout")}
                        </Button>
                    </div>
                </header>
                <main className="flex-1 p-4 pb-24 sm:p-6 sm:pb-24 lg:p-8 max-w-[1600px] w-full mx-auto aj-app-content">
                    <Outlet />
                </main>
                <BrandFooter />
            </div>
            <BottomNav onOpenMore={() => setOpen(true)} />
            <Toaster position="top-right" richColors />
        </div>
    );
}
